import { action, makeObservable, override, runInAction } from 'mobx';
import { createContext, useContext } from 'react';
import { bpHistory } from 'global/bpHistory';
import ElectionsModule from 'store/modules/elections/electionsModule';
import EnrollmentPeriodModule from 'store/modules/enrollment/enrollmentPeriodModule';
import IFullFormPageStoreWithElections from 'store/modules/full-employee-form/iFullFormPageStoreWithElections';
import { BaseFullEmployeeFormDomainParams } from 'store/modules/full-employee-form/types';

export type FullEmployeeFormDomainParams = BaseFullEmployeeFormDomainParams;

export default class FullFormPageStore extends IFullFormPageStoreWithElections<FullEmployeeFormDomainParams> {
  public readonly enrollmentPeriodModule = new EnrollmentPeriodModule();

  public override async getInitialData(): Promise<void> {
    const electionsModule = this._electionsModule;
    await this.enrollmentPeriodModule.uploadEnrollmentPeriod(this._domainData.periodId);
    runInAction(() => {
      const cartId = this.enrollmentPeriodModule.enrollmentProposalData?.confirmedProposal?.cartId;
      if (!cartId) {
        throw new Error('Confirmed proposal cart id is not found');
      }
      electionsModule.setCartId(cartId);
    });
    await Promise.all([
      electionsModule.getRequiredSubInfo(),
      electionsModule.getClasses(),
      electionsModule.getProducts(),
    ]);
    await super.getInitialData();
  }

  protected getPrimaryFormId(): string | undefined {
    return this._electionsModule.requiredSubInfo?.editSubFormId;
  }

  public onBackRedirect(): void {
    bpHistory.push(`/add-employees/${this._domainData.groupId}/${this._domainData.periodId}`);
  }

  // Refreshes rates after save
  protected override onProductLineRateAnswersSaved(): Promise<void> {
    return this._electionsModule.getStatistics();
  }

  constructor(domainData: FullEmployeeFormDomainParams) {
    super(
      domainData,
      new ElectionsModule({
        groupId: domainData.groupId,
        enrollmentPeriodId: domainData.periodId,
      }),
    );

    makeObservable<FullFormPageStore>(this, {
      onBackRedirect: action.bound,
      getInitialData: override,
    });
  }
}

const FullFormPageStoreContext = createContext(new FullFormPageStore({} as FullEmployeeFormDomainParams));

export const useFullFormPageStore = (): FullFormPageStore => {
  return useContext(FullFormPageStoreContext);
};

export { FullFormPageStoreContext };
